import { createContext, useContext } from 'react'
import type { ScreenType } from './useScreenSequence'
import type {
  CommunicationChannelPreference,
  CommunicationFrequencyPreference,
  NativeLanguage,
  SelfAssessedLevel,
  WeeklyTimeCommitment,
  PrimaryGoal,
  IndustryInterest,
  TestType,
  NeedsAnalysisSubmissionRequest,
} from '@/types/learner-portal'

/**
 * State shape for the Needs Analysis survey
 * Tracks user answers, UI state, and conditional logic
 */
export type NeedsAnalysisState = {
  // User answers (matches backend DTO)
  readonly native_language: NativeLanguage | null // Q0
  readonly self_assessed_english_level: SelfAssessedLevel | null // Q2 (A0-C1)
  readonly weekly_time_commitment: WeeklyTimeCommitment | null // Q3
  readonly primary_goals: readonly PrimaryGoal[] // Q4 - array (max 2)
  readonly goal_specification: TestType | null // Q6 - test type (conditional)
  readonly industry_interests: readonly IndustryInterest[] // Q5 - multiple industries
  readonly communication_channels: readonly CommunicationChannelPreference[] // Q7 - multi-select
  readonly communication_frequency: CommunicationFrequencyPreference | null // Q8

  // UI state
  readonly currentStep: number

  // State flags
  readonly isSubmitting: boolean
  readonly isDirty: boolean // Track if user has made changes
}

/**
 * Actions available for updating the Needs Analysis state
 */
export type NeedsAnalysisActions = {
  // Field setters
  setNativeLanguage: (language: NativeLanguage | null) => void
  setEnglishLevel: (level: SelfAssessedLevel) => void
  setTimeCommitment: (time: WeeklyTimeCommitment) => void
  setGoals: (goals: PrimaryGoal[]) => void // Enforces max 2
  toggleIndustry: (industry: IndustryInterest) => void
  setTestType: (test: TestType | null) => void
  toggleCommunicationChannel: (channel: CommunicationChannelPreference) => void
  setCommunicationFrequency: (frequency: CommunicationFrequencyPreference) => void

  // Navigation
  nextStep: () => void
  previousStep: () => void
  goToStep: (step: number) => void

  // Lifecycle
  reset: () => void
  submitSurvey: () => Promise<void>

  // Validation
  canProceedFromCurrentStep: () => boolean
  isFormValid: () => boolean

  // Data access
  getSubmissionData: () => NeedsAnalysisSubmissionRequest
}

/**
 * Screen sequence derived from the answers. The screens array is the single
 * source of truth: the modal renders it and validation indexes into it.
 */
export type NeedsAnalysisScreens = {
  readonly screens: readonly ScreenType[]
  readonly totalSteps: number
}

/**
 * Combined context type with state and actions
 */
export type NeedsAnalysisContextType = NeedsAnalysisState & NeedsAnalysisScreens & NeedsAnalysisActions

/**
 * React Context for Needs Analysis survey state management
 */
export const NeedsAnalysisContext = createContext<NeedsAnalysisContextType | null>(null)

/**
 * Hook to access the Needs Analysis context
 * Throws an error if used outside of NeedsAnalysisProvider
 */
export function useNeedsAnalysis(): NeedsAnalysisContextType {
  const context = useContext(NeedsAnalysisContext)
  if (!context) {
    throw new Error('useNeedsAnalysis must be used within a NeedsAnalysisProvider')
  }
  return context
}
